// @ts-nocheck
// Internal shared building blocks for the AI Teacher pages.

import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import Icon from '../../components/common/Icon';

// Trust strip
export function TrustStrip({ label = "Aligned with", items }: { label?: string; items?: { label: string }[] }) {
  return (
    <div className="ip-trust">
      <span className="ip-trust__label">{label}</span>
      <div className="ip-trust__logos">
        {(items || []).map((it, i) => (
          <span key={i} className="ip-trust__text">{it.label}</span>
        ))}
      </div>
    </div>
  );
}

// Page hero
export function PageHero({
  eyebrow,
  eyebrowBadge,
  icon,
  title,
  lead,
  actions,
  trust,
  right,
  solo,
}: {
  eyebrow?: ReactNode;
  eyebrowBadge?: ReactNode;
  icon?: string;
  title?: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  trust?: { label?: string; items?: { label: string }[] };
  right?: ReactNode;
  solo?: boolean;
}) {
  return (
    <header className={"ip-hero" + (solo ? " ip-hero--solo" : "")}>
      <div className="ip-hero__text">
        <div className="ip-hero__eyebrow-row">
          {eyebrowBadge ? <span className="ip-eyebrow-badge">{eyebrowBadge}</span> : null}
          <span className="ip-eyebrow">{icon ? <Icon name={icon} size={13} stroke={2} /> : null}{eyebrow}</span>
        </div>
        <h1 className="ip-hero__title">{title}</h1>
        {lead ? <p className="ip-hero__lead">{lead}</p> : null}
        {actions ? <div className="ip-hero__actions">{actions}</div> : null}
        {trust ? <TrustStrip label={trust.label} items={trust.items} /> : null}
      </div>
      <div className="ip-hero__tools">
        <button className="ip-explainer" type="button" title="Open explainer video">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="6 4 20 12 6 20 6 4" />
          </svg>
          Explainer video
        </button>
      </div>
      {right ? <div className="ip-hero__right">{right}</div> : null}
    </header>
  );
}

// Section wrapper
export function Section({
  eyebrow,
  title,
  hint,
  children,
  style,
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  hint?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section className="ip-section" style={style}>
      <div className="ip-section__head">
        <div>
          {eyebrow ? <span className="ip-eyebrow">{eyebrow}</span> : null}
          {title ? <h2 className="ip-section__title">{title}</h2> : null}
        </div>
        {hint ? <p className="ip-section__hint">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

// Progress ring
export function Ring({
  value,
  size = 120,
  stroke = 9,
  valSize = 30,
  cap,
}: {
  value: number;
  size?: number;
  stroke?: number;
  valSize?: number;
  cap?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <div className="ip-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-inset)" strokeWidth={stroke} />
        <circle
          className="ip-ring__circle"
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="var(--accent)" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div style={{ textAlign: "center" }}>
        <div className="ip-ring__val" style={{ fontSize: valSize }}>{value}<sup>%</sup></div>
        {cap ? <div className="ip-ring__cap">{cap}</div> : null}
      </div>
    </div>
  );
}

// Sparkline
export function Sparkline({
  points,
  width = 120,
  height = 34,
  color = "var(--accent)",
  fill = true,
}: {
  points: number[];
  width?: number;
  height?: number;
  color?: string;
  fill?: boolean;
}) {
  const max = Math.max(...points), min = Math.min(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((p, i) => [i * step, height - 4 - ((p - min) / span) * (height - 8)]);
  const d = coords.map((c, i) => (i === 0 ? "M" : "L") + c[0].toFixed(1) + " " + c[1].toFixed(1)).join(" ");
  const area = d + ` L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg className="ip-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {fill ? <path d={area} fill={color} opacity="0.10" /> : null}
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2.6" fill={color} />
    </svg>
  );
}

// Send to parent button
export function SendBtn({ onClick, label = "Send to parent" }: { onClick?: () => void; label?: string }) {
  return (
    <button className="ip-btn ip-btn--wa" onClick={onClick}>
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.6L2 22l4.5-1.5A11 11 0 1 0 20.5 3.5zM12 20a8 8 0 0 1-4.1-1.1l-.3-.2-2.7.9.9-2.6-.2-.3A8 8 0 1 1 12 20z"/></svg>
      {label}
    </button>
  );
}

export function PresentBtn({ onClick, label = "Present to parent" }: { onClick?: () => void; label?: string }) {
  return (
    <button className="ip-btn ip-btn--accent" onClick={onClick}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="6 4 20 12 6 20 6 4"/></svg>
      {label}
    </button>
  );
}

export function SentBadge() {
  return (
    <span className="ip-sent-badge" title="Already sent to parent">
      <Icon name="check" size={12} stroke={2.5} />
      Sent
    </span>
  );
}

// Competitive path
export function CompetitivePath({ steps = ["Boards", "NEET", "JEE", "Olympiad"] }: { steps?: string[] }) {
  return (
    <div className="ip-path">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className="ip-path__node">
            <div className="ip-path__dot">{i + 1}</div>
            <span className="ip-path__lbl">{s}</span>
          </div>
          {i < steps.length - 1 ? <div className="ip-path__line" /> : null}
        </React.Fragment>
      ))}
    </div>
  );
}
